import { usePostRobot } from '@lib/sdk/utils/postrobot'
import {
  type UiSelectAgendaRequest,
  type UiSelectAgendaResponse,
  type UiSelectCollectionPlayerRequest,
  type UiSelectCollectionPlayerResponse,
  type UiSelectContentRequest,
  type UiSelectContentResponse,
  type UiOpenRequest,
  type UiToastRequest,
  type UiPreselectSfdcMeetingIdRequest,
  type UiPreselectSfdcMeetingIdResponse,
  type UiOpenGlobalPopupRequest,
  type UiOpenGlobalPopupResponse,
  UI_API_METHOD_TYPES,
} from '@sdk/api/modules/ui/types.ui'

/**
 * @hidden
 * @deprecated
 */
export async function ui_select_content(payload: UiSelectContentRequest = {}): Promise<UiSelectContentResponse> {
  const pr = await usePostRobot()
  console.warn(
    '[content.ui.ts]: ui_select_content is deprecated and might get removed in future versions, please use select_content instead.',
  )
  const response = await pr.send(window.parent, UI_API_METHOD_TYPES.UI_SELECT_CONTENT, payload)
  return <UiSelectContentResponse>response.data
}

/**
 * Allows the user to select (and preselect) content from the instance.
 *
 * Opens the content selector dialog to allow the user to select content and handle the outcome. This method allows you to
 * prompt the user to select content from the CatalogIQ instance and use that content in your application.
 *
 * @example
 * const api = useApi() // or useUi() if you know that you 're in UI context
 * api
 *   .selectContent({
 *     selections: [
 *       { fileId: "01HCZ623YYRFJQ0F7B69VWE510", type: "file" },
 *       {
 *         fileId: "01HD3XGZ5FN90QJGK7CH8TBZDF",
 *         type: "page",
 *         pageIndex: 2,
 *       },
 *     ],
 *   })
 *
 * @param payload optional payload to preselect specific content
 * @returns Promise with the user action and selected content
 */
export async function selectContent(payload: UiSelectContentRequest = {}): Promise<UiSelectContentResponse> {
  const pr = await usePostRobot()
  const response = await pr.send(window.parent, UI_API_METHOD_TYPES.UI_SELECT_CONTENT, payload)
  return <UiSelectContentResponse>response.data
}

/**
 * Open a file in the CatalogIQ instance.
 */
export async function open(payload: UiOpenRequest): Promise<void> {
  const pr = await usePostRobot()
  await pr.send(window.parent, UI_API_METHOD_TYPES.UI_OPEN, payload)
}

/**
 * Show a toast message to the user.
 *
 * @example
 * useUi().toast({ type: 'info', message: 'Hello, world!' })
 */
export async function toast(payload: UiToastRequest): Promise<void> {
  const pr = await usePostRobot()
  await pr.send(window.parent, UI_API_METHOD_TYPES.UI_TOAST, payload)
}

/**
 * Preselect a meeting in the top meeting bar by Salesforce Meeting ID. From there, the user can easily start the meeting
 * with a single click.
 *
 * @example
 * const objectFetchedFromSalesforce = {
 *   // fetch dynamically from Salesforce or provide in another way
 *   EventId: '00U1r00000D1J1zEAF',
 * }
 * ui.preselectSfdcMeetingId({ eventId: objectFetchedFromSalesforce.EventId }).then((response) => {
 *   // e.g. display advanced meeting details inside your app
 *   console.log(response.event)
 * })
 */
export async function preselectSfdcMeetingId(
  payload: UiPreselectSfdcMeetingIdRequest,
): Promise<UiPreselectSfdcMeetingIdResponse> {
  const pr = await usePostRobot()
  const response = await pr.send(window.parent, UI_API_METHOD_TYPES.UI_PRESELECT_SFDC_MEETING_ID, payload)
  return <UiPreselectSfdcMeetingIdResponse>response.data
}

/**
 * Opens the collection player selector modal to allow the user to select collection player content and handle the outcome.
 * ```
 * const api = useApi() // or useUi() if you know that you 're in UI context
 *
 * uiApi.selectCollectionContent({
 *  // optional payload
 *  initial_data: {
 *   name: 'test',
 *   groups: []
 *  }
 * })
 * ```
 */
export async function selectCollectionContent(
  payload: UiSelectCollectionPlayerRequest = {},
): Promise<UiSelectCollectionPlayerResponse> {
  const pr = await usePostRobot()
  const response = await pr.send(window.parent, UI_API_METHOD_TYPES.UI_SELECT_COLLECTION_PLAYER_CONTENT, payload)

  return <UiSelectCollectionPlayerResponse>response.data
}

/**
 * Opens the agenda selector modal to allow the user to select agenda content and handle the outcome.
 * ```
 * const api = useApi() // or useUi() if you know that you 're in UI context
 *
 * uiApi.selectAgendaContent({
 *  // optional payload
 *  initial_agenda: {
 *   name: 'test',
 *   groups: []
 *  }
 * })
 * ```
 */
export async function selectAgendaContent(payload: UiSelectAgendaRequest = {}): Promise<UiSelectAgendaResponse> {
  const pr = await usePostRobot()
  const response = await pr.send(window.parent, UI_API_METHOD_TYPES.UI_SELECT_AGENDA_CONTENT, payload)

  return <UiSelectAgendaResponse>response.data
}

/**
 * Opens a global popup app by name.
 *
 * @example
 * const api = useApi()
 * api.openGlobalPopup('my-popup-app')
 * api.openGlobalPopup('my-popup-app', { customData: 'value' })
 *
 * @param appName - The name of the app to open (must have app_type: 'ui-global-popup')
 * @param extraContext - Extra context to pass to the app
 * @returns Promise with the result of the operation
 */
export async function openGlobalPopup(
  appName: string,
  extraContext?: Record<string, any>,
): Promise<UiOpenGlobalPopupResponse> {
  const pr = await usePostRobot()
  const payload: UiOpenGlobalPopupRequest = {
    app_name: appName,
    extra_context: extraContext,
  }
  const response = await pr.send(window.parent, UI_API_METHOD_TYPES.UI_OPEN_GLOBAL_POPUP, payload)
  return <UiOpenGlobalPopupResponse>response.data
}

/**
 * Closes the currently open global popup app.
 *
 * @example
 * const api = useApi()
 * api.closeGlobalPopup()
 */
export async function closeGlobalPopup(): Promise<void> {
  const pr = await usePostRobot()
  await pr.send(window.parent, UI_API_METHOD_TYPES.UI_CLOSE_GLOBAL_POPUP, {})
}
